import { Kunde } from 'api/kunde';
import { ArtikelDetail } from 'api/artikelDetail';
import { ArtikelDetailLieferant } from 'api/artikelDetailLieferant';
import { GrowthWithColor } from 'reports/utils';

export type UmsatzSummarySource = 'auftrag' | 'rechnung' | 'bestellung';

export const sourceOptions = [
    { value: 'auftrag', label: 'Auftrag' },
    { value: 'rechnung', label: 'Rechnung' },
    { value: 'bestellung', label: 'Bestellung' },
];

export type UmsatzSummaryGroupBy =
    | 'kunde'
    | 'produktgruppe'
    | 'artikelGruppe'
    | 'branche'
    | 'produzent'
    | 'lieferant'
    | 'betreuer'
    | 'telBetreuer'
    | 'land';

export const groupByOptions = [
    { value: 'kunde', label: 'Kunde' },
    { value: 'produktgruppe', label: 'Produktgruppe' },
    { value: 'artikelGruppe', label: 'Artikelgruppe' },
    { value: 'branche', label: 'Branche' },
    { value: 'produzent', label: 'Produzent' },
    { value: 'lieferant', label: 'Lieferant' },
    { value: 'betreuer', label: 'Betreuer' },
    { value: 'telBetreuer', label: 'Tel.-Betreuer' },
    { value: 'land', label: 'Land/Bundesland' },
];

export interface FilterValues {
    source: UmsatzSummarySource;
    groupBy: UmsatzSummaryGroupBy;
    sortColumn: string;
    sortDirection: 'asc' | 'desc';
    datumVon: string;
    datumBis: string;
    kNr?: number;
    kName?: string;
    kundePlzVon?: string;
    kundePlzBis?: string;
    kundeUmsatzTsdVon: number;
    kundeUmsatzTsdBis: number;
    kundeBranchen?: number[];
    kundeInteresseFuerProdukte?: number[];
    kundeErweiterteSuchen?: number[];
    kundeBetreuer?: number;
    kundeTelBetreuer?: number;
    betreuer?: number;
    betreuerGebiet?: boolean;
    telBetreuer?: number;
    telBetreuerGebiet?: boolean;
    produktgruppen?: number[];
    hauptlieferantNr?: number;
    hauptlieferantName?: string;
    showDetails?: boolean;
    addMissingRows1?: boolean;
    addMissingRows3?: boolean;
    showEkNetto?: boolean;
    showVkNetto?: boolean;
    showBMarge?: boolean;
    showNMarge?: boolean;
    showBAnteil?: boolean;
    showFAnteil?: boolean;
}

export interface UmsatzSummaryRecord {
    kNr: number;
    nr: string;
    datum: string;
    betreuer: number;
    telBetreuer: number;
    sNetto: number;
    zahlBed: number;
    fracht: number;
    tipp: number;
    roadPr: number;

    kunde?: Kunde;
}

export interface UmsatzSummaryPosition {
    kNr: number;
    kName: string;
    recordNr: string;
    datum: string;
    betreuer: number;
    telBetreuer: number;
    pos: number;
    artikelGruppeNr: number;
    artikelNr: string;
    posText: string;
    posEinh: string;
    posMenge: number;
    posEkPreis: number;
    posEPreis: number;
    posNetto: number;
    lArt?: string; // auftrag only
    artikelDetail?: ArtikelDetail;
    hauptlieferant?: ArtikelDetailLieferant;
    lineData?: LineData;
}

export interface LineData {
    groupById: string;
    groupByLabel: string;
    groupByNote?: string;
    groupByLink?: string;
    recordNrList: string[];
    count: number;
    countP: number;
    ekNetto: number;
    ekNettoP: number;
    vkNetto: number;
    vkNettoP: number;
    bMarge: number;
    bMargeP: number;
    nMarge: number;
    nMargeP: number;
    bAnteil: number;
    fAnteil: number;
}

export const defaultLineData: LineData = {
    groupById: '',
    groupByLabel: '',
    recordNrList: [],
    count: 0,
    countP: 0,
    ekNetto: 0,
    ekNettoP: 0,
    vkNetto: 0,
    vkNettoP: 0,
    bMarge: 0,
    bMargeP: 0,
    nMarge: 0,
    nMargeP: 0,
    bAnteil: 0,
    fAnteil: 0,
};

export interface LineDataWithGrowth extends LineData {
    countPrevGrowth: GrowthWithColor;
    countAvgGrowth: GrowthWithColor;
    ekNettoPrevGrowth: GrowthWithColor;
    ekNettoAvgGrowth: GrowthWithColor;
    vkNettoPrevGrowth: GrowthWithColor;
    vkNettoAvgGrowth: GrowthWithColor;
    bMargePrevGrowth: GrowthWithColor;
    bMargeAvgGrowth: GrowthWithColor;
    nMargePrevGrowth: GrowthWithColor;
    nMargeAvgGrowth: GrowthWithColor;
    bAnteilPrevGrowth: GrowthWithColor;
    bAnteilAvgGrowth: GrowthWithColor;
    fAnteilPrevGrowth: GrowthWithColor;
    fAnteilAvgGrowth: GrowthWithColor;
}

export interface UmsatzSummarySourceData {
    isLoading: boolean;
    total: LineData;
    recordList: UmsatzSummaryRecord[];
    recordPositionList: UmsatzSummaryPosition[];
}

export interface UmsatzSummary {
    loading: boolean;
    total: LineData;
    recordList: UmsatzSummaryRecord[];
    recordPositionList: UmsatzSummaryPosition[];
    lineDataList: LineData[];
}
